import { REST_URI } from 'lib/appConfig';
import session from 'lib/api/links/session';
import { getAuthHeaders } from 'lib/api/links/helpers';

/**
 * POST /operator-view/labor-ticket/{machineRef}/start-jobs
 * @param {number} machineRef
 * @param {Array<{ workOrderOperationRef: number, activityMode: string }>} jobs
 */
export const postMultiJobStartJobs = async (machineRef, jobs) => {
  const apiKey = await session.getApiKey();
  const res = await fetch(
    `${REST_URI}/operator-view/labor-ticket/${machineRef}/start-jobs`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(apiKey),
      },
      body: JSON.stringify({ jobs }),
    }
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `start-jobs failed: ${res.status}`);
  }
};

/**
 * Assign program event(s) to a work order operation (operator change job).
 * Stamps workOrderOperationRef and confirmedAt via confirm — no labor ticket
 * (submit-intervals creates LTs).
 * POST /operator-view/labor-ticket/{machineRef}/program-events/confirm
 * @param {number} machineRef
 * @param {number|string} eventId  – representative programEventRef (fallback when sourceEventRefs omitted)
 * @param {number} targetWorkOrderOperationRef – the new WOO to assign
 * @param {Array<number|string>} [sourceEventRefs] – all refs to confirm (defaults to [eventId])
 */
/**
 * POST /operator-view/labor-ticket/{machineRef}/program-events/split
 * @param {number} machineRef
 * @param {string} splitTime - ISO timestamp for the split point
 * @param {number} workOrderOperationRef - original WOO
 * @param {number|null} [newWorkOrderOperationRef] - new WOO (split-change-job)
 */
export const postSplitProgramEvents = async (
  machineRef,
  splitTime,
  workOrderOperationRef,
  newWorkOrderOperationRef
) => {
  const apiKey = await session.getApiKey();
  const res = await fetch(
    `${REST_URI}/operator-view/labor-ticket/${machineRef}/program-events/split`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(apiKey),
      },
      body: JSON.stringify({
        splitTime,
        workOrderOperationRef,
        ...(newWorkOrderOperationRef ? { newWorkOrderOperationRef } : {}),
      }),
    }
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `split failed: ${res.status}`);
  }
};

export const postSubmitIntervals = async (machineRef, payload) => {
  const apiKey = await session.getApiKey();
  const res = await fetch(
    `${REST_URI}/operator-view/labor-ticket/${machineRef}/program-events/submit-intervals`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(apiKey),
      },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `submit-intervals failed: ${res.status}`);
  }
  const contentType = res.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return res.json();
  }
  return null;
};

export const postChangeJobForEvent = async (
  machineRef,
  eventId,
  targetWorkOrderOperationRef,
  sourceEventRefs
) => {
  const apiKey = await session.getApiKey();
  const res = await fetch(
    `${REST_URI}/operator-view/labor-ticket/${machineRef}/program-events/confirm`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(apiKey),
      },
      body: JSON.stringify({
        eventRefs: sourceEventRefs || [eventId],
        workOrderOperationRef: targetWorkOrderOperationRef,
      }),
    }
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `change job failed: ${res.status}`);
  }
};
